import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';

import { captureHydrationException, getSliceState } from '../helpers';
import { updateShift } from '../shifts/actions';
import { createShift, selectShiftById } from '../shifts/slice';

import { SLICE_NAME } from './constants';
import { ShiftNote, ShiftNoteSerializedEntity, ShiftNotesState } from './types';

export const shiftNotesAdapter = createEntityAdapter<ShiftNote>();

export const initialState: ShiftNotesState = {
  ...shiftNotesAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchSchedule.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action.payload?.included) {
        try {
          const shiftNotes = (
            action?.payload?.included as ShiftNoteSerializedEntity[]
          )
            .filter(
              (entity: ShiftNoteSerializedEntity) => entity.type === 'shiftNote'
            )
            .map((entity: ShiftNoteSerializedEntity) => ({
              id: entity.id,
              type: entity.type,
              text: entity.attributes.text,
              createdAt: entity.attributes.createdAt,
              author: entity.attributes.author,
              ...Object.fromEntries(
                Object.entries(entity.relationships || {}).map(
                  ([k, v]: [string, any]) => [k, v.data]
                )
              ),
            }));
          shiftNotesAdapter.setAll(state, shiftNotes);
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    });

    builder.addCase(createShift.fulfilled, (state, action: any) => {
      const shift = action.payload?.data;
      if (shift?.attributes?.note && shift?.relationships?.note?.data?.id) {
        const noteId = shift.relationships.note.data.id;
        const existingNote = state.entities[noteId];
        if (existingNote) {
          shiftNotesAdapter.updateOne(state, {
            id: noteId,
            changes: {
              text: shift.attributes.note,
            },
          });
        }
      }

      if (action.payload?.included) {
        const shiftNotes = action.payload.included
          .filter((entity: any) => entity.type === 'shiftNote')
          .map((entity: any) => ({
            id: entity.id,
            type: entity.type,
            text: entity.attributes.text,
            createdAt: entity.attributes.createdAt,
            author: entity.attributes.author,
          }));

        if (shiftNotes.length > 0) {
          shiftNotesAdapter.upsertMany(state, shiftNotes);
        }
      }
    });

    builder.addCase(updateShift.fulfilled, (state, action: any) => {
      const shift = action.payload?.data;
      if (shift?.attributes?.note && shift?.relationships?.note?.data?.id) {
        const noteId = shift.relationships.note.data.id;
        const existingNote = state.entities[noteId];
        if (existingNote) {
          shiftNotesAdapter.updateOne(state, {
            id: noteId,
            changes: {
              text: shift.attributes.note,
            },
          });
        }
      }

      if (action.payload?.included) {
        const shiftNotes = action.payload.included
          .filter((entity: any) => entity.type === 'shiftNote')
          .map((entity: any) => ({
            id: entity.id,
            type: entity.type,
            text: entity.attributes.text,
            createdAt: entity.attributes.createdAt,
            author: entity.attributes.author,
          }));

        if (shiftNotes.length > 0) {
          shiftNotesAdapter.upsertMany(state, shiftNotes);
        }
      }
    });
  },
});

export const reducer = slice.reducer;

export const {
  selectById: selectShiftNoteById,
  selectIds: selectShiftNoteIds,
  selectEntities: selectShiftNoteEntities,
  selectAll: selectAllShiftNotes,
  selectTotal: selectTotalShiftNotes,
} = shiftNotesAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const selectShiftNoteTextByShiftId = createCachedSelector(
  (state: RootState, props: { shiftId: EntityId }) => {
    if (!props?.shiftId) return '';
    const shift = selectShiftById(state, props.shiftId);
    const noteId = shift?.note?.id;
    if (!noteId) return '';
    const note = selectShiftNoteById(state, noteId);
    return note?.text || '';
  },
  (text: string) => text || ''
)(
  (_, props) => `selectShiftNoteTextByShiftId-${props?.shiftId || 'undefined'}`
);
